import { useEffect, useMemo, useState } from 'react';

import usePrometheusQuery, { fetchPrometheusQuery } from '../../../utils/hooks/use_prometheus_query_without_range';
import { combineRangeAndOffset, unixTimestampsToPrometheusRange } from '../../../utils/prometheus_utils';

const getTotalInferenceLogStorage = (timestamp) => {
  if (timestamp !== null) {
    return `sum by(topic) (topic_bytes@${timestamp})`;
  }
  return `sum by(topic) (topic_bytes)`;
}

const getUsedCPUQuery = (rangeVector, offset) => {
  return `avg_over_time(sum by(namespace)(wallaroo_kube_pod_resource_usage{resource="cpu"})${combineRangeAndOffset(rangeVector, offset)})`;
}

const getRequestedCPUQuery = (rangeVector, offset) => {
  return `avg_over_time(sum by(namespace)(wallaroo_kube_pod_resource_requests{resource="cpu"})${combineRangeAndOffset(rangeVector, offset)})`;
}

const getUsedMemQuery = (rangeVector, offset) => {
  return `avg_over_time(sum by(namespace)(wallaroo_kube_pod_resource_usage{resource="memory"})${combineRangeAndOffset(rangeVector, offset)})`;
}

const getRequestedMemQuery = (rangeVector, offset) => {
  return `avg_over_time(sum by(namespace)(wallaroo_kube_pod_resource_requests{resource="memory"})${combineRangeAndOffset(rangeVector, offset)})`;
}

const getUsedGPUQuery = (rangeVector, offset) => {
  return `avg_over_time(sum by(namespace)(wallaroo_kube_pod_resource_requests{resource=~"nvidia.com/gpu|qualcomm.com/qaic"})${combineRangeAndOffset(rangeVector, offset)})`;
}

const getRequestedGPUQuery = (rangeVector, offset) => {
  return `avg_over_time(sum by(namespace)(wallaroo_kube_pod_resource_requests{resource=~"nvidia.com/gpu|qualcomm.com/qaic"})${combineRangeAndOffset(rangeVector, offset)})`;
}

const usePrometheusQueryByNamespace = ({ startTimestamp, endTimestamp, query }) => {
  const [data, setData] = useState([])
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setData([]);
    setError(null);
    fetchPrometheusQuery(query).then((res) => {
      setLoading(false);
      setData(res.values);
      setError(null);
    }).catch((e) => {
      setLoading(false);
      setError(e);
      setData([]);
      return null;
    })
  }, [startTimestamp, endTimestamp, query])

  return {
    data, isLoading, error
  }
}

export const useTotalLogStorageByTopicName = ({ startTimestamp, endTimestamp }) => {
  const query = useMemo(() => {
    const rangeObj = unixTimestampsToPrometheusRange(startTimestamp, endTimestamp);
    return getTotalInferenceLogStorage(endTimestamp);
  }, [startTimestamp, endTimestamp])

  return usePrometheusQueryByNamespace({ startTimestamp, endTimestamp, query })
}

export const useAverageUsedCPUByNamespace = ({ startTimestamp, endTimestamp }) => {
  const query = useMemo(() => {
    const rangeObj = unixTimestampsToPrometheusRange(startTimestamp, endTimestamp);
    return getUsedCPUQuery(rangeObj.rangeVector, rangeObj.offset);
  }, [startTimestamp, endTimestamp])

  return usePrometheusQueryByNamespace({ startTimestamp, endTimestamp, query })
}

export const useAverageReqCPUByNamespace = ({ startTimestamp, endTimestamp }) => {
  const query = useMemo(() => {
    const rangeObj = unixTimestampsToPrometheusRange(startTimestamp, endTimestamp);
    return getRequestedCPUQuery(rangeObj.rangeVector, rangeObj.offset);
  }, [startTimestamp, endTimestamp])

  return usePrometheusQueryByNamespace({ startTimestamp, endTimestamp, query })
}

export const useAverageUsedMemByNamespace = ({ startTimestamp, endTimestamp }) => {
  const query = useMemo(() => {
    const rangeObj = unixTimestampsToPrometheusRange(startTimestamp, endTimestamp);
    return getUsedMemQuery(rangeObj.rangeVector, rangeObj.offset);
  }, [startTimestamp, endTimestamp])

  return usePrometheusQueryByNamespace({ startTimestamp, endTimestamp, query })
}

export const useAverageReqMemByNamespace = ({ startTimestamp, endTimestamp }) => {
  const query = useMemo(() => {
    const rangeObj = unixTimestampsToPrometheusRange(startTimestamp, endTimestamp);
    return getRequestedMemQuery(rangeObj.rangeVector, rangeObj.offset);
  }, [startTimestamp, endTimestamp])

  return usePrometheusQueryByNamespace({ startTimestamp, endTimestamp, query })
}

export const useAverageUsedGPUByNamespace = ({ startTimestamp, endTimestamp }) => {
  const query = useMemo(() => {
    const rangeObj = unixTimestampsToPrometheusRange(startTimestamp, endTimestamp);
    return getUsedGPUQuery(rangeObj.rangeVector, rangeObj.offset);
  }, [startTimestamp, endTimestamp])

  return usePrometheusQueryByNamespace({ startTimestamp, endTimestamp, query })
}

export const useAverageReqGPUByNamespace = ({ startTimestamp, endTimestamp }) => {
  const query = useMemo(() => {
    const rangeObj = unixTimestampsToPrometheusRange(startTimestamp, endTimestamp);
    return getRequestedGPUQuery(rangeObj.rangeVector, rangeObj.offset);
  }, [startTimestamp, endTimestamp])

  return usePrometheusQueryByNamespace({ startTimestamp, endTimestamp, query })
}

export const useLogStorageForPipeline = ({
  deploymentId,
  pipelineName,
  startTimestamp,
  endTimestamp,
}) => {
  const query = useMemo(() => {
    const namespace = `${pipelineName}-${deploymentId}`
    const rangeObj = unixTimestampsToPrometheusRange(startTimestamp, endTimestamp);
    return getTotalInferenceLogStorage(namespace, endTimestamp);
  }, [endTimestamp, startTimestamp, deploymentId, pipelineName])
  const prometheusResponse = usePrometheusQuery(query);

  return {
    chartData: prometheusResponse?.data,
    isLoading: prometheusResponse?.isLoading,
    message: prometheusResponse?.error || null,
  }
}

export const convertPrometheusResponseToNumber = (resp) => {
  const dataArr = resp?.chartData;
  const numVal = dataArr?.length ? dataArr[1] : null;

  return numVal;
}
